import { InfNFe } from '../../@types/jsonContent';

type DanfePreviewParams = {
  infNFe: InfNFe;
  notaFiscalId: string | number;
};

function formatCPFCNPJ(value: string): string {
  const nums = value.replace(/\D/g, '');
  if (nums.length === 11) {
    return nums.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (nums.length === 14) {
    return nums.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return value;
}

function formatCEP(value: string): string {
  const nums = value.replace(/\D/g, '');
  if (nums.length === 8) {
    return nums.replace(/(\d{5})(\d{3})/, '$1-$2');
  }
  return value;
}

function formatFone(value: string): string {
  const nums = value.replace(/\D/g, '');
  if (nums.length === 11) {
    return nums.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }
  if (nums.length === 10) {
    return nums.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return value;
}

function formatDate(isoDate: string): string {
  try {
    const d = new Date(isoDate);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  } catch {
    return isoDate;
  }
}

function formatMoney(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0,00';
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatQtd(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '0,000';
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

function getModalidadeFrete(mod: string): string {
  const map: Record<string, string> = {
    '0': '0 - Contratacao do Frete por conta do Remetente (CIF)',
    '1': '1 - Contratacao do Frete por conta do Destinatario (FOB)',
    '2': '2 - Contratacao do Frete por conta de Terceiros',
    '3': '3 - Transporte Proprio por conta do Remetente',
    '4': '4 - Transporte Proprio por conta do Destinatario',
    '9': '9 - Sem Ocorrencia de Transporte',
  };
  return map[mod] || mod;
}

function getFormaPagamento(tPag: string): string {
  const map: Record<string, string> = {
    '01': 'Dinheiro',
    '02': 'Cheque',
    '03': 'Cartao de Credito',
    '04': 'Cartao de Debito',
    '05': 'Credito Loja',
    '10': 'Vale Alimentacao',
    '11': 'Vale Refeicao',
    '12': 'Vale Presente',
    '13': 'Vale Combustivel',
    '14': 'Duplicata Mercantil',
    '15': 'Boleto Bancario',
    '16': 'Deposito Bancario',
    '17': 'Pagamento Instantaneo (PIX)',
    '18': 'Transferencia bancaria, Carteira Digital',
    '19': 'Programa de fidelidade, Cashback, Credito Virtual',
    '90': 'Sem pagamento',
    '99': 'Outros',
  };
  return map[tPag] || `Outros (${tPag})`;
}

function escapeHtml(value: string | number | undefined | null): string {
  const str = String(value ?? '');
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function pickCaseInsensitive(obj: any, keys: string[], fallback: string = ''): string {
  if (!obj || typeof obj !== 'object') return fallback;
  const lower = Object.keys(obj).reduce((acc: any, k) => {
    acc[k.toLowerCase()] = k;
    return acc;
  }, {});
  const target = keys.map(k => k.toLowerCase()).find(k => lower[k]);
  return target ? (obj[lower[target]] ?? fallback) : fallback;
}

function getICMSInfo(imposto: any): { orig: string; cst: string; vBC: string; pICMS: string; vICMS: string } {
  const icms = imposto?.ICMS;
  if (!icms) return { orig: '', cst: '', vBC: '0,00', pICMS: '0,00', vICMS: '0,00' };

  const key = Object.keys(icms)[0];
  if (!key) return { orig: '', cst: '', vBC: '0,00', pICMS: '0,00', vICMS: '0,00' };

  const data = icms[key];
  return {
    orig: pickCaseInsensitive(data, ['orig'], '0'),
    cst: pickCaseInsensitive(data, ['CST', 'CSOSN', 'cst', 'csosn'], ''),
    vBC: formatMoney(pickCaseInsensitive(data, ['vBC', 'vbc'], '0')),
    pICMS: formatMoney(pickCaseInsensitive(data, ['pICMS', 'picms'], '0')),
    vICMS: formatMoney(pickCaseInsensitive(data, ['vICMS', 'vicms'], '0')),
  };
}

export function gerarDanfePreviewHTML({ infNFe, notaFiscalId }: DanfePreviewParams): string {
  const { ide, emit, dest, det, total, transp, pag, cobr } = infNFe;
  const tot = total.ICMSTot;

  const isHomologacao = ide.tpAmb === '2';
  const informacoesComplementares = (infNFe as any)?.infAdic?.infCpl || '';

  const itensRows = det
    .map((item) => {
      const p = item.prod;
      const icmsInfo = getICMSInfo(item.imposto);
      const pisData = item.imposto?.PIS;
      const pisKey = pisData ? Object.keys(pisData)[0] : null;
      const pis = pisKey ? (pisData as any)[pisKey] : null;
      const cofinsData = item.imposto?.COFINS;
      const cofinsKey = cofinsData ? Object.keys(cofinsData)[0] : null;
      const cofins = cofinsKey ? (cofinsData as any)[cofinsKey] : null;

      return `
        <tr>
          <td class="center">${escapeHtml(p.cProd)}</td>
          <td>${escapeHtml(p.xProd)}</td>
          <td class="center">${escapeHtml(p.NCM)}</td>
          <td class="center">${escapeHtml(icmsInfo.cst)}</td>
          <td class="center">${escapeHtml(p.CFOP)}</td>
          <td class="center">${escapeHtml(p.uCom)}</td>
          <td class="right">${formatQtd(p.qCom)}</td>
          <td class="right">${formatMoney(p.vUnCom)}</td>
          <td class="right">${formatMoney(p.vProd)}</td>
          <td class="right">${icmsInfo.vBC}</td>
          <td class="right">${icmsInfo.vICMS}</td>
          <td class="right">${formatMoney(pis?.vPIS || '0')}</td>
          <td class="right">${formatMoney(cofins?.vCOFINS || '0')}</td>
        </tr>`;
    })
    .join('');

  const pagRows = (pag.detPag || [])
    .map(
      (dp) => `
      <tr>
        <td>${getFormaPagamento(dp.tPag)}</td>
        <td class="right">R$ ${formatMoney(dp.vPag)}</td>
      </tr>`,
    )
    .join('');

  const emitDoc = emit.cpf
    ? `CPF: ${formatCPFCNPJ(emit.cpf)}`
    : emit.cnpj
      ? `CNPJ: ${formatCPFCNPJ(emit.cnpj)}`
      : '';

  const destDoc = dest.cpf
    ? `CPF: ${formatCPFCNPJ(dest.cpf)}`
    : dest.cnpj
      ? `CNPJ: ${formatCPFCNPJ(dest.cnpj)}`
      : '';

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>DANFE - Nota ${escapeHtml(ide.nNF)} Serie ${escapeHtml(ide.serie)}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 11px;
      color: #333;
      background: #f5f5f5;
      padding: 10px;
    }
    .danfe-container {
      max-width: 210mm;
      margin: 0 auto;
      background: white;
      border: 2px solid #333;
      padding: 8px;
    }

    ${
      isHomologacao
        ? `
    .watermark {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) rotate(-30deg);
      font-size: 80px;
      font-weight: bold;
      color: rgba(255, 0, 0, 0.08);
      white-space: nowrap;
      pointer-events: none;
      z-index: 0;
    }
    `
        : ''
    }

    .header-box {
      border: 1.5px solid #333;
      margin-bottom: 4px;
    }
    .header-top {
      display: flex;
      border-bottom: 1px solid #333;
    }
    .header-emit {
      flex: 1;
      padding: 8px;
      border-right: 1px solid #333;
      text-align: center;
    }
    .header-emit .nome { font-size: 16px; font-weight: bold; }
    .header-emit .fantasia { font-size: 13px; color: #555; margin-bottom: 3px; }
    .header-emit .endereco { font-size: 10px; color: #666; line-height: 1.4; }
    .header-danfe {
      width: 140px;
      padding: 6px;
      text-align: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      border-right: 1px solid #333;
    }
    .header-danfe .title { font-size: 14px; font-weight: bold; }
    .header-danfe .subtitle { font-size: 8px; line-height: 1.3; margin-top: 3px; }
    .header-danfe .entrada-saida {
      margin-top: 4px;
      font-size: 10px;
    }
    .header-danfe .entrada-saida span {
      display: inline-block;
      border: 1px solid #333;
      padding: 2px 8px;
      font-weight: bold;
      font-size: 13px;
    }
    .header-info {
      flex: 1;
      padding: 6px;
      display: flex;
      flex-direction: column;
      justify-content: center;
    }
    .header-info .nfe-num {
      font-size: 10px;
      font-weight: bold;
      margin-bottom: 4px;
    }
    .header-info .nfe-num span { font-size: 14px; }

    .validation-banner {
      background: #fff3cd;
      border: 2px dashed #ff9800;
      color: #856404;
      text-align: center;
      padding: 6px;
      margin-bottom: 4px;
      font-size: 12px;
      font-weight: bold;
    }

    .section {
      border: 1.5px solid #333;
      margin-bottom: 4px;
    }
    .section-title {
      background: #e8e8e8;
      padding: 2px 6px;
      font-size: 9px;
      font-weight: bold;
      text-transform: uppercase;
      border-bottom: 1px solid #333;
      color: #555;
    }
    .section-grid {
      display: flex;
      flex-wrap: wrap;
    }
    .field {
      border-right: 1px solid #ccc;
      border-bottom: 1px solid #ccc;
      padding: 2px 5px;
      min-height: 32px;
    }
    .field:last-child { border-right: none; }
    .field-label {
      font-size: 8px;
      color: #888;
      text-transform: uppercase;
      margin-bottom: 1px;
    }
    .field-value {
      font-size: 10px;
      font-weight: 500;
    }
    .inf-adic {
      padding: 6px;
      font-size: 10px;
      color: #333;
      white-space: pre-wrap;
      word-break: break-word;
    }

    .items-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 9px;
    }
    .items-table th {
      background: #e8e8e8;
      padding: 3px 4px;
      border: 1px solid #999;
      font-size: 8px;
      text-transform: uppercase;
      font-weight: bold;
    }
    .items-table td {
      padding: 3px 4px;
      border: 1px solid #ddd;
    }
    .items-table .center { text-align: center; }
    .items-table .right { text-align: right; }

    .totais-grid {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
    }
    .totais-grid .field { min-height: 28px; }

    .pag-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 10px;
    }
    .pag-table th {
      background: #e8e8e8;
      padding: 3px 6px;
      border: 1px solid #999;
      text-align: left;
      font-size: 9px;
    }
    .pag-table td {
      padding: 3px 6px;
      border: 1px solid #ddd;
    }
    .pag-table .right { text-align: right; }

    .footer-info {
      font-size: 9px;
      color: #666;
      text-align: center;
      padding: 6px;
      border-top: 1px solid #ccc;
      margin-top: 4px;
    }

    @media print {
      body { background: white; padding: 0; }
      .danfe-container { border: 1.5px solid #333; }
      .no-print { display: none !important; }
    }

    .print-btn {
      display: block;
      margin: 10px auto;
      padding: 10px 30px;
      background: #0288d1;
      color: white;
      border: none;
      border-radius: 5px;
      font-size: 14px;
      cursor: pointer;
    }
    .print-btn:hover { background: #0277bd; }
  </style>
</head>
<body>
  <button class="print-btn no-print" onclick="window.print()">Imprimir</button>

  ${isHomologacao ? '<div class="watermark">SEM VALOR FISCAL - HOMOLOGACAO</div>' : ''}

  <div class="danfe-container">
    <!-- BANNER DE VALIDACAO -->
    <div class="validation-banner">
      DOCUMENTO AUXILIAR DA NOTA FISCAL ELETRONICA - PRE-VISUALIZACAO<br>
      SEM VALOR FISCAL - NOTA NAO AUTORIZADA
    </div>

    <!-- CABECALHO -->
    <div class="header-box">
      <div class="header-top">
        <div class="header-emit">
          ${emit.xfant ? `<div class="fantasia">${escapeHtml(emit.xfant)}</div>` : ''}
          <div class="nome">${escapeHtml(emit.xnome)}</div>
          <div class="endereco">
            ${escapeHtml(emit.enderEmit.xlgr)}, ${escapeHtml(emit.enderEmit.nro)}<br>
            ${escapeHtml(emit.enderEmit.xbairro)} - ${escapeHtml(emit.enderEmit.xmun)}/${escapeHtml(emit.enderEmit.uf)}<br>
            CEP: ${formatCEP(emit.enderEmit.cep)} - Fone: ${formatFone(emit.enderEmit.fone || '')}<br>
            ${escapeHtml(emitDoc)} ${emit.ie ? ` | IE: ${escapeHtml(emit.ie)}` : ''}
          </div>
        </div>
        <div class="header-danfe">
          <div class="title">DANFE</div>
          <div class="subtitle">Documento Auxiliar da<br>Nota Fiscal Eletronica</div>
          <div class="entrada-saida">
            ${ide.tpNF === '1' ? 'SAIDA' : 'ENTRADA'}: <span>${ide.tpNF === '1' ? '1' : '0'}</span>
          </div>
        </div>
        <div class="header-info">
          <div class="nfe-num">N.: <span>${escapeHtml(ide.nNF)}</span></div>
          <div class="nfe-num">SERIE: <span>${escapeHtml(ide.serie)}</span></div>
          <div style="font-size:9px;color:#888;margin-top:4px;">
            Emissao: ${formatDate(ide.dhEmi)}
          </div>
          <div style="font-size:9px;color:#888;">
            Ambiente: ${isHomologacao ? 'HOMOLOGACAO' : 'PRODUCAO'}
          </div>
        </div>
      </div>
    </div>

    <!-- DESTINATARIO -->
    <div class="section">
      <div class="section-title">Destinatario / Remetente</div>
      <div class="section-grid">
        <div class="field" style="flex:2">
          <div class="field-label">Nome / Razao Social</div>
          <div class="field-value">${escapeHtml(dest.xnome)}</div>
        </div>
        <div class="field" style="flex:0.8">
          <div class="field-label">${destDoc.startsWith('CNPJ') ? 'CNPJ' : 'CPF'}</div>
          <div class="field-value">${escapeHtml(destDoc.split(': ')[1] || '-')}</div>
        </div>
      </div>
      <div class="section-grid">
        <div class="field" style="flex:2">
          <div class="field-label">Endereco</div>
          <div class="field-value">${escapeHtml(dest.enderDest?.xlgr || '-')}, ${escapeHtml(dest.enderDest?.nro || '')}</div>
        </div>
        <div class="field" style="flex:1">
          <div class="field-label">Bairro</div>
          <div class="field-value">${escapeHtml(dest.enderDest?.xbairro || '-')}</div>
        </div>
        <div class="field" style="flex:0.5">
          <div class="field-label">CEP</div>
          <div class="field-value">${formatCEP(dest.enderDest?.cep || '')}</div>
        </div>
      </div>
      <div class="section-grid">
        <div class="field" style="flex:1.5">
          <div class="field-label">Municipio</div>
          <div class="field-value">${escapeHtml(dest.enderDest?.xmun || '-')}</div>
        </div>
        <div class="field" style="flex:0.3">
          <div class="field-label">UF</div>
          <div class="field-value">${escapeHtml(dest.enderDest?.uf || '-')}</div>
        </div>
        <div class="field" style="flex:0.8">
          <div class="field-label">Telefone</div>
          <div class="field-value">${formatFone(dest.enderDest?.fone || '')}</div>
        </div>
      </div>
    </div>

    <!-- NATUREZA DA OPERACAO -->
    <div class="section">
      <div class="section-grid">
        <div class="field" style="flex:1">
          <div class="field-label">Natureza da Operacao</div>
          <div class="field-value">${escapeHtml(ide.natOp)}</div>
        </div>
      </div>
    </div>

    <!-- PRODUTOS / SERVICOS -->
    <div class="section">
      <div class="section-title">Dados dos Produtos / Servicos</div>
      <table class="items-table">
        <thead>
          <tr>
            <th>Codigo</th>
            <th>Descricao do Produto/Servico</th>
            <th>NCM</th>
            <th>CST</th>
            <th>CFOP</th>
            <th>UN</th>
            <th>Qtde</th>
            <th>Vr. Unit</th>
            <th>Vr. Total</th>
            <th>BC ICMS</th>
            <th>Vr. ICMS</th>
            <th>Vr. PIS</th>
            <th>Vr. COFINS</th>
          </tr>
        </thead>
        <tbody>
          ${itensRows}
        </tbody>
      </table>
    </div>

    <!-- TOTAIS -->
    <div class="section">
      <div class="section-title">Calculo do Imposto</div>
      <div class="totais-grid">
        <div class="field">
          <div class="field-label">Base Calc. ICMS</div>
          <div class="field-value">${formatMoney(tot.vBC)}</div>
        </div>
        <div class="field">
          <div class="field-label">Valor ICMS</div>
          <div class="field-value">${formatMoney(tot.vICMS)}</div>
        </div>
        <div class="field">
          <div class="field-label">BC ICMS ST</div>
          <div class="field-value">${formatMoney(tot.vBCST)}</div>
        </div>
        <div class="field">
          <div class="field-label">Valor ICMS ST</div>
          <div class="field-value">${formatMoney(tot.vST)}</div>
        </div>
        <div class="field">
          <div class="field-label">Valor Total Prod.</div>
          <div class="field-value">${formatMoney(tot.vProd)}</div>
        </div>
      </div>
      <div class="totais-grid">
        <div class="field">
          <div class="field-label">Valor Frete</div>
          <div class="field-value">${formatMoney(tot.vFrete)}</div>
        </div>
        <div class="field">
          <div class="field-label">Valor Seguro</div>
          <div class="field-value">${formatMoney(tot.vSeg)}</div>
        </div>
        <div class="field">
          <div class="field-label">Desconto</div>
          <div class="field-value">${formatMoney(tot.vDesc)}</div>
        </div>
        <div class="field">
          <div class="field-label">Outras Desp.</div>
          <div class="field-value">${formatMoney(tot.vOutro)}</div>
        </div>
        <div class="field">
          <div class="field-label" style="font-size:9px;font-weight:bold;color:#333;">Valor Total NF</div>
          <div class="field-value" style="font-size:13px;font-weight:bold;">R$ ${formatMoney(tot.vNF)}</div>
        </div>
      </div>
      <div class="totais-grid">
        <div class="field">
          <div class="field-label">Valor PIS</div>
          <div class="field-value">${formatMoney(tot.vPIS)}</div>
        </div>
        <div class="field">
          <div class="field-label">Valor COFINS</div>
          <div class="field-value">${formatMoney(tot.vCOFINS)}</div>
        </div>
        <div class="field">
          <div class="field-label">Valor IPI</div>
          <div class="field-value">${formatMoney(tot.vIPI)}</div>
        </div>
        <div class="field">
          <div class="field-label">Valor II</div>
          <div class="field-value">${formatMoney(tot.vII)}</div>
        </div>
        <div class="field">
          <div class="field-label">ICMS Desonerado</div>
          <div class="field-value">${formatMoney(tot.vICMSDeson)}</div>
        </div>
      </div>
    </div>

    <!-- TRANSPORTE -->
    <div class="section">
      <div class="section-title">Transportador / Volumes Transportados</div>
      <div class="section-grid">
        <div class="field" style="flex:1">
          <div class="field-label">Modalidade do Frete</div>
          <div class="field-value">${getModalidadeFrete(transp.modFrete)}</div>
        </div>
      </div>
    </div>

    <!-- FATURA -->
    ${
      cobr?.fat
        ? `
    <div class="section">
      <div class="section-title">Fatura</div>
      <div class="section-grid">
        <div class="field" style="flex:1">
          <div class="field-label">Numero</div>
          <div class="field-value">${escapeHtml(cobr.fat.nFat)}</div>
        </div>
        <div class="field" style="flex:1">
          <div class="field-label">Valor Original</div>
          <div class="field-value">R$ ${formatMoney(cobr.fat.vOrig)}</div>
        </div>
        <div class="field" style="flex:1">
          <div class="field-label">Desconto</div>
          <div class="field-value">R$ ${formatMoney(cobr.fat.vDesc)}</div>
        </div>
        <div class="field" style="flex:1">
          <div class="field-label">Valor Liquido</div>
          <div class="field-value">R$ ${formatMoney(cobr.fat.vLiq)}</div>
        </div>
      </div>
    </div>
    `
        : ''
    }

    <!-- PAGAMENTO -->
    <div class="section">
      <div class="section-title">Formas de Pagamento</div>
      <table class="pag-table">
        <thead>
          <tr>
            <th>Forma de Pagamento</th>
            <th style="text-align:right">Valor</th>
          </tr>
        </thead>
        <tbody>
          ${pagRows}
        </tbody>
      </table>
    </div>
    ${
      informacoesComplementares
        ? `
    <div class="section">
      <div class="section-title">Informacoes Complementares</div>
      <div class="inf-adic">${escapeHtml(informacoesComplementares)}</div>
    </div>
    `
        : ''
    }

    <!-- RODAPE -->
    <div class="footer-info">
      <strong>NOTA FISCAL DE PRE-VISUALIZACAO - SEM VALOR FISCAL</strong><br>
      Nota Fiscal ID: ${escapeHtml(notaFiscalId)} | Modelo: ${escapeHtml(ide.mod)} | Serie: ${escapeHtml(ide.serie)} | Numero: ${escapeHtml(ide.nNF)}
    </div>
  </div>

  <button class="print-btn no-print" onclick="window.print()">Imprimir</button>
</body>
</html>`;
}

export function abrirDanfePreview(infNFe: InfNFe, notaFiscalId: string | number): void {
  const html = gerarDanfePreviewHTML({ infNFe, notaFiscalId });
  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.write(html);
    newWindow.document.close();
  }
}
